import React from 'react';
import { AlertCircle, Scale, FileText, Building2, Shield, Gavel } from 'lucide-react';

const Disclaimer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <div className="min-h-screen bg-white py-32" data-testid="disclaimer-page">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-5xl font-bold mb-8">Disclaimer</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: {currentYear}</p>
        
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
          <div className="flex">
            <AlertCircle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" />
            <div>
              <p className="font-bold text-yellow-800 mb-2">Important Notice</p>
              <p className="text-sm text-yellow-800">Please read this disclaimer carefully before using ZenStudios&apos; services. By accessing or using our platform, you acknowledge and agree to these terms. This disclaimer is governed by Indian laws including RERA, Consumer Protection Act, and IT Act.</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-8 text-gray-700 leading-relaxed">
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-black">1. General Disclaimer</h2>
            </div>
            <p>The information provided on the ZenStudios platform is for general informational purposes only. While we strive to keep the information up-to-date and accurate, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the platform or the information, products, services, or related graphics contained on the platform.</p>
            <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
              <strong>Note:</strong> ZenStudios operates as a technology platform facilitating connections between property seekers, agents, developers, and service providers. We are not a real estate agent or broker licensed under any state Real Estate Regulatory Authority.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-black">2. Real Estate Regulatory Authority (RERA) Compliance</h2>
            </div>
            <p className="mb-4">Under the Real Estate (Regulation and Development) Act, 2016, and respective State RERA rules:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>RERA Registration:</strong> All real estate projects with plot area exceeding 500 sq.m. or more than 8 apartments must be registered with the respective State RERA before advertisement or sale. Users must verify RERA registration numbers independently.</li>
              <li><strong>Advertisement Compliance:</strong> All advertisements must contain RERA registration number. Any project listed without valid RERA number (where applicable) is the sole responsibility of the listing party.</li>
              <li><strong>Promoter Obligations:</strong> Developers must register with RERA, maintain separate escrow accounts (70% of collections), provide quarterly updates, and adhere to project timelines.</li>
              <li><strong>Agent Registration:</strong> Real estate agents must be registered under respective State RERA. Users should verify agent RERA registration before engaging.</li>
              <li><strong>Grievance Redressal:</strong> In case of any disputes related to RERA-registered projects, parties may approach the respective State RERA Authority or RERA Appellate Tribunal.</li>
            </ul>
            <div className="mt-4 bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Important:</strong> ZenStudios does not verify RERA registration status of properties or agents listed on our platform. Users must independently verify RERA registration from the official State RERA portal before any transaction.
              </p>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-black">3. Property Transaction Guidelines (Indian Law)</h2>
            </div>
            <p className="mb-4">Users must comply with the following Indian laws governing property transactions:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Transfer of Property Act, 1882:</strong> All immovable property transfers must be executed through a registered deed. Oral agreements are not legally enforceable for property transactions.</li>
              <li><strong>Registration Act, 1908:</strong> All property sale deeds, agreements, and lease deeds exceeding 12 months must be registered with the Sub-Registrar office.</li>
              <li><strong>Indian Stamp Act, 1899:</strong> Applicable stamp duty must be paid as per state regulations. Deficient stamping renders documents inadmissible in court.</li>
              <li><strong>Income Tax Act, 1961:</strong>
                <ul className="list-circle ml-6 mt-2 space-y-1 text-sm">
                  <li>TDS under Section 194-IA: Buyer must deduct 1% TDS on property transactions exceeding ₹50 lakhs.</li>
                  <li>Capital gains tax applies on property sale as per holding period.</li>
                  <li>PAN is mandatory for property transactions exceeding ₹10 lakhs.</li>
                </ul>
              </li>
              <li><strong>Benami Transactions (Prohibition) Act, 1988:</strong> Properties held in the name of others to conceal real ownership are prohibited and subject to confiscation.</li>
              <li><strong>FEMA Regulations:</strong> Non-Resident Indians (NRIs) and foreign nationals must comply with RBI/FEMA guidelines for property purchase in India.</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <Gavel className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-black">4. Consumer Protection & E-Commerce Rules</h2>
            </div>
            <p className="mb-4">ZenStudios operates in compliance with:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Consumer Protection Act, 2019:</strong> Users have the right to file complaints against unfair trade practices, defective services, or misleading advertisements before District Consumer Forums, State Commission, or National Commission based on claim value.</li>
              <li><strong>Consumer Protection (E-Commerce) Rules, 2020:</strong> As an e-commerce entity, ZenStudios maintains grievance redressal mechanism, does not manipulate prices, and discloses terms clearly.</li>
              <li><strong>Information Technology Act, 2000:</strong> We maintain reasonable security practices for data protection. Any unauthorized access to our systems is punishable under Sections 43, 66, and 72 of IT Act.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">5. AI-Powered Cost Estimates</h2>
            <p><strong>Interior Design Quotes:</strong></p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>All cost estimates generated by our AI system are <strong>approximate</strong> and based on standard market rates</li>
              <li>Actual costs may vary significantly based on:
                <ul className="list-circle ml-6 mt-1 space-y-1">
                  <li>Material selection and brand preferences</li>
                  <li>Site conditions and accessibility</li>
                  <li>Design complexity and customization</li>
                  <li>Market fluctuations in material and labor costs</li>
                  <li>Unforeseen structural or regulatory requirements</li>
                </ul>
              </li>
              <li>Quotes are valid for 30 days from generation date</li>
              <li>A detailed, binding quote will be provided only after physical site inspection</li>
              <li>We reserve the right to revise estimates based on actual project requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">6. Location-Based Pricing</h2>
            <p>Our pricing varies by city and region based on local market conditions, material availability, and labor rates. The multipliers applied are:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li><strong>Tier 1 Cities</strong> (Mumbai, Delhi, Bangalore, Chennai, Kolkata, Hyderabad): 1.15x - 1.3x base rate</li>
              <li><strong>Tier 2 Cities:</strong> 1.0x base rate</li>
              <li><strong>Tier 3 Cities:</strong> 0.85x base rate</li>
            </ul>
            <p className="mt-2">These multipliers are subject to change based on market dynamics.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">7. Real Estate Listings</h2>
            <p>For real estate services:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Property information is provided by sellers, agents, and developers. ZenStudios acts only as a facilitator.</li>
              <li>We do not verify or guarantee the accuracy of property details, ownership claims, or legal status.</li>
              <li>Property values, prices, and availability are subject to change without notice.</li>
              <li>Buyers must conduct independent due diligence including:
                <ul className="list-circle ml-6 mt-1 space-y-1 text-sm">
                  <li>Title verification through competent legal counsel</li>
                  <li>Encumbrance certificate check (minimum 30 years)</li>
                  <li>RERA registration verification (for applicable projects)</li>
                  <li>Physical inspection and measurement verification</li>
                  <li>Verification of approvals from competent authorities</li>
                </ul>
              </li>
              <li>We are not responsible for discrepancies in property descriptions, photographs, or specifications.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">8. Property Management Services</h2>
            <p>Regarding property management:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Service quality may vary based on property location and condition</li>
              <li>We are not liable for damages caused by tenants, acts of God, or third parties</li>
              <li>Rental income is subject to market conditions, tenant creditworthiness, and payment behavior</li>
              <li>Additional costs may apply for emergency repairs, legal matters, or special services</li>
              <li>Property management agreements are subject to terms specified in individual service contracts</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-black">9. Limitation of Liability</h2>
            </div>
            <p>To the fullest extent permitted by applicable Indian law, ZenStudios and its affiliates shall not be liable for:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Any direct, indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, data, business opportunities, or goodwill</li>
              <li>Errors or omissions in content, estimates, or property information</li>
              <li>Service interruptions, technical issues, or system failures</li>
              <li>Actions, inactions, fraud, or misrepresentation by third-party service providers, agents, or developers</li>
              <li>Market fluctuations affecting project costs or property values</li>
              <li>Delays or defaults by contractors, vendors, or service providers</li>
            </ul>
            <p className="mt-4 text-sm bg-red-50 p-4 rounded-lg text-red-800">
              <strong>Maximum Liability:</strong> In no event shall ZenStudios&apos; total liability exceed the amount paid by the user for the specific service giving rise to the claim, or ₹10,000, whichever is lower.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">10. No Warranty</h2>
            <p>Services are provided &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; without warranties of any kind, either express or implied, including but not limited to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Merchantability or fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
              <li>Accuracy, completeness, or reliability of information</li>
              <li>Uninterrupted or error-free operation of the platform</li>
              <li>Results, outcomes, or success of property transactions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">11. Third-Party Content & Services</h2>
            <p>Our platform may contain links to third-party websites or integrate with third-party services. We:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Do not control, endorse, or assume responsibility for third-party content</li>
              <li>Are not responsible for the availability, accuracy, or policies of external sites</li>
              <li>Do not guarantee the security, privacy practices, or compliance of third-party services</li>
              <li>Recommend reviewing third-party terms, privacy policies, and credentials before engagement</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">12. Force Majeure</h2>
            <p>We are not liable for delays or failures in performance resulting from circumstances beyond our reasonable control, including but not limited to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Natural disasters, floods, earthquakes, or severe weather</li>
              <li>Government actions, regulatory changes, or new legislation</li>
              <li>Wars, civil unrest, terrorism, or political instability</li>
              <li>Strikes, labor disputes, or lockouts</li>
              <li>Supply chain disruptions, material shortages, or logistic failures</li>
              <li>Pandemics, epidemics, or public health emergencies</li>
              <li>Internet failures, cyber attacks, or infrastructure breakdowns</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">13. User Responsibilities</h2>
            <p>Users are solely responsible for:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Verifying all property information, legal status, and ownership claims independently</li>
              <li>Conducting proper due diligence with qualified legal and financial professionals</li>
              <li>Obtaining necessary permits, approvals, and clearances for property transactions</li>
              <li>Complying with applicable local building codes, zoning regulations, and RERA guidelines</li>
              <li>Maintaining adequate insurance coverage for properties and transactions</li>
              <li>Ensuring tax compliance including TDS, stamp duty, and registration charges</li>
              <li>Verifying RERA registration of projects and agents where applicable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">14. Dispute Resolution & Governing Law</h2>
            <p className="mb-4">This disclaimer is governed by and construed in accordance with the laws of India.</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Arbitration:</strong> Any dispute arising out of or in connection with this disclaimer shall be referred to and finally resolved by arbitration under the Arbitration and Conciliation Act, 1996. The seat of arbitration shall be Hyderabad, Telangana.</li>
              <li><strong>Jurisdiction:</strong> Subject to arbitration, courts in Hyderabad, Telangana shall have exclusive jurisdiction over any disputes.</li>
              <li><strong>RERA Disputes:</strong> For disputes related to RERA-registered projects, users may approach the respective State RERA Authority.</li>
              <li><strong>Consumer Disputes:</strong> Consumer complaints may be filed before appropriate Consumer Forums as per Consumer Protection Act, 2019.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">15. Amendments</h2>
            <p>ZenStudios reserves the right to modify, update, or change this disclaimer at any time without prior notice. The updated disclaimer will be effective immediately upon posting on the platform. Continued use of our services constitutes acceptance of the modified terms.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">16. Severability</h2>
            <p>If any provision of this disclaimer is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such invalidity shall not affect the validity of remaining provisions, which shall continue in full force and effect.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">17. Contact & Grievance Redressal</h2>
            <p>For questions, concerns, or grievances regarding this disclaimer or our services:</p>
            <div className="mt-4 bg-gray-50 p-6 rounded-lg">
              <p><strong>Grievance Officer:</strong> Legal Department, ZenStudios</p>
              <p className="mt-2">
                <strong>Email:</strong> grievance@zenstudios.in<br />
                <strong>Phone:</strong> +91 87904 60259<br />
                <strong>Address:</strong> Hyderabad, Telangana, India
              </p>
              <p className="mt-4 text-sm text-gray-600">
                Response time: Grievances will be acknowledged within 48 hours and resolved within 30 days as per applicable regulations.
              </p>
            </div>
          </section>
        </div>
        
        <div className="mt-12 pt-8 border-t text-center text-sm text-gray-500">
          <p>© {currentYear} ZenStudios™. All rights reserved.</p>
          <p className="mt-2">
            This disclaimer was last reviewed and updated in compliance with Indian real estate laws and regulations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;
